import React, { useMemo, useState, useRef, useCallback, memo, Suspense } from 'react';
import store from '@store';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import {
  TouchableDiv,
  TouchableButton,
} from '@components/common/Touchable/index';
import { APP_CONFIG } from '@constants';
import { flowType } from '@utility/userInfo';
import { setCurrentPage, removeSeat, clearCart } from '@reducers/orders';
import { setErrorPopUp } from '@reducers/notification';
import { addInProcessItemsToCart, addInProcessItemsToCartAsync } from '../../MenuOrderHandler';
import { LABEL } from '@veriables';
import { ReactComponent as MoreVertical } from '@assets/images/moreVertical.svg';
import style from './MenuCartBarHeader.module.scss';
import MoreActions from '@containers/MoreActions';
import getDeviceType from '@utility/deviceType';
import LoadingScreen from '@components/common/Loader/newLoaderModal';
import VoidCheckModal from '../Modals/VoidCheckModal/index';
import { setVoidCheckOpen } from '@reducers/orders';

const MenuCartbarHeader = ({
  check,
  serverAppTheme,
  setCurrentPage,
  removeSeat,
  clearCart,
  cart,
  setVoidCheckOpen,
}) => {
  const navigate = useNavigate();
  const flowTypeValue = useMemo(() => {
    return flowType();
  }, []);
  const seatsData = useMemo(
    () => JSON.parse(window.sessionStorage.getItem('seat')),
    []
  );
  const [isToolsModalOpen, setToolsModalOpen] = useState(false);
  const [isCloseCheckModal, setIsCloseCheckModal] = useState(false);
  const [isCloseLoading, setIsCloseLoading] = useState(false);
  const isClosingRef = useRef(false);
  const hasApiFailedRef = useRef(false);
  const deviceType = getDeviceType();

  const newSeatStyle = useMemo(
    () => ({
      seatStyle: {
        background: serverAppTheme?.iconcolor_bg_box,
        color: serverAppTheme?.iconcolor,
        width: '2rem',
        height: '2rem',
      },
      tableNameStyle: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: serverAppTheme?.primary_text,
        maxWidth: '120px',
      },
      crossIconStyle: {
        color: serverAppTheme?.primary_text,
        width: '2rem',
        height: '2rem',
        background: serverAppTheme?.iconcolor_bg_box,
      },
      editIconStyle: {
        color: serverAppTheme?.primary_text,
        fontSize: '16px',
      },
      sendTabBg: {
        background: serverAppTheme?.gameCard_selected_tab,
        color: serverAppTheme?.primary_text,
        border: `2px solid ${serverAppTheme?.itemFacts}`,
      },
      pendingTabBg: {
        color: serverAppTheme?.primary_text,
        background: 'transparent',
        border: `2px solid ${serverAppTheme?.itemFacts} `,
      },
    }),
    [serverAppTheme]
  );
  const onMoreInfoClick = useCallback(
    e => {
      e.preventDefault();
      const cb = () => {};
      setToolsModalOpen(true);
      addInProcessItemsToCart(cb, cart);
    },
    [cart]
  );
  const CloseButton = memo(({ onClose }) => (
    <div className={cn('ml-auto d-flex px-0 align-items-start pl-1')}>
      <div
        className={cn(
          'col-12 px-0 d-flex justify-content-end align-items-center rounded'
        )}
      >
        <TouchableButton
          type="button"
          className={cn(
            'col-12 d-flex justify-content-around align-items-center px-0 rounded btn-ripple fs_14 fw-500',
            style.closeButtonStyle
          )}
          onClick={e => {
            e.preventDefault();
            onClose();
          }}
          rippleEffect={true}
        >
          {LABEL().OS_CLOSE}
        </TouchableButton>
      </div>
    </div>
  ));
  const TableName = memo(
    ({ tableNameStyle, check }) => (
      <div
        className={cn('px-0 fs_16 fw-500 mt-1')} style={{ ...tableNameStyle, display: 'block'}}
      >
        {check?.tab
          ? check?.tabName
            : `${LABEL().TABLE} ${
                check?.stationName
            ? check?.stationName
            : check?.stationNumber && check?.stationNumber?.length <= 16
              ? check?.stationNumber
              : check?.stationNumber?.substring(0, 8) + '...'
          }`}
      </div>
    )
  );
  const SeatIndicator = memo(({ seatStyle, onClick, children }) => (
    <TouchableDiv
      style={seatStyle}
      className={cn(
        'px-0',
        style.colorVoiletGgray,
        'd-flex align-items-center justify-content-center rounded mr-1 col-3'
      )}
      onClick={onClick}
    >
      {children}
    </TouchableDiv>
  ));
  const handleCloseClick = useCallback(() => {
    if (isClosingRef.current) return;

    const { orders } = store.getState();
    const { cart } = orders;

    if ((deviceType.isTogo || deviceType?.isCallCenter || deviceType?.isSpeedOrderMode || deviceType?.isDriveThru || deviceType?.isCounter) && !isCloseCheckModal) {
      setVoidCheckOpen(true);
      setIsCloseCheckModal(true);
      return;
    }

    isClosingRef.current = true;

    const hasPendingItems = cart?.some(e => e.isProcessing);

    if (!hasPendingItems || hasApiFailedRef.current) {
      window.sessionStorage.removeItem('menuScrollTop');
      window.sessionStorage.removeItem('itemCount');
      setCurrentPage(0);
      removeSeat();
      clearCart();
      navigate(`${APP_CONFIG.BASE_URL}/${flowTypeValue}/checks`, { replace: true });
      isClosingRef.current = false;
      return;
    }

    setIsCloseLoading(true);
    addInProcessItemsToCartAsync(cart)
      .then(() => {
        window.sessionStorage.removeItem('menuScrollTop');
        window.sessionStorage.removeItem('itemCount');
        setCurrentPage(0);
        removeSeat();
        clearCart();
        navigate(`${APP_CONFIG.BASE_URL}/${flowTypeValue}/checks`, { replace: true });
        isClosingRef.current = false;
      })
      .catch(err => {
        setIsCloseLoading(false);
        hasApiFailedRef.current = true;
        isClosingRef.current = false;
        setErrorPopUp({ res: err?.message });
      });
  }, [isCloseCheckModal]);
  return (
    <div className={cn('w-100 align-items-start px-0 d-flex flex-row mb-2')}>
      {isCloseLoading && <LoadingScreen />}
      {seatsData?.sourceType === 0 && (
        <SeatIndicator
          seatStyle={{
            ...newSeatStyle.seatStyle,
            flexBasis: '40px',
            height: '32px',
          }}
          onClick={onMoreInfoClick}
        >
          <MoreVertical
            fill={serverAppTheme?.iconcolor}
            role="presentation"
            style={{ width: '20px', height: '20px' }}
            alt="appIcon"
          />
        </SeatIndicator>
      )}
      {!deviceType.isTogo && !deviceType.isCallCenter && !deviceType?.isSpeedOrderMode && !deviceType?.isDriveThru && !deviceType?.isCounter && (
        <TableName
          tableNameStyle={newSeatStyle.tableNameStyle}
          check={check}
        />
      )}
      <CloseButton onClose={handleCloseClick} />

      {isToolsModalOpen && (
        <MoreActions
          serverAppTheme={serverAppTheme}
          isOpen={isToolsModalOpen}
          closeTools={(shouldRedirect) => {
            setToolsModalOpen(false);
            if (shouldRedirect) {
              navigate(`${APP_CONFIG.BASE_URL}/${flowTypeValue}/checks`, { replace: true });
            }
          }}
          data={check}
        />
      )}

      <Suspense fallback={<LoadingScreen />}>
        {isCloseCheckModal && (
          <VoidCheckModal
            isVoidOpen={isCloseCheckModal}
            setIsVoidOpen={setIsCloseCheckModal}
            resetCheckBoxState={() => {}}
            isTogo={deviceType?.isTogo}
            isCallCenter={deviceType?.isCallCenter}
            isSpeedOrderMode={deviceType?.isSpeedOrderMode}
            isDriveThru = {deviceType?.isDriveThru}
            isCounter = {deviceType?.isCounter}
          />
        )}
      </Suspense>
    </div>
  );
};

const mapStateToProps = ({ orders, theme }) => ({
  check: orders.seats,
  serverAppTheme: theme.serverAppTheme,
  cart: orders.cart,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      setCurrentPage,
      removeSeat,
      clearCart,
      setVoidCheckOpen,
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(memo(MenuCartbarHeader));
